import React, {useRef, useState, useEffect} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Reveal } from './components/Reveal';
import { Navbar } from './components/Navbar';
import { updates } from './data/updates';
import { sendEmail } from './email';
import { AnimatePresence, motion } from 'framer-motion';


const contactSchema = z.object({
  from_name: z.string().min(2, "Name is required"),
  reply_to: z.string().email("Invalid email"),
  message: z.string().min(10, "Message is too short"),
});

type ContactFormData = z.infer<typeof contactSchema>;

function App() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) return savedTheme;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema)
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      await sendEmail({
        from_name: data.from_name,
        reply_to: data.reply_to,
        message: data.message,
      });
      alert("Message Sent!");
      reset();
    } catch (error) {
      // Log the real cause (missing .env vars, bad template, network, ...) but
      // keep the customer-facing message generic outside development.
      console.error("FAILED...", error);
      alert(
        process.env.NODE_ENV === 'development' && error instanceof Error
          ? error.message
          : "Error sending message."
      );
    }
  };

  const newsletterRef = useRef<HTMLInputElement>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const onJoinNow = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValue = newsletterRef.current?.value.trim();

    if (!emailValue) return;

    setIsSubscribing(true);
    try {
      await sendEmail({
        from_name: "Newsletter Subscriber",
        reply_to: emailValue,
        message: `New Newsletter Signup: ${emailValue}`,
      });
      alert("Welcome to Aether! You've been added.");
      if (newsletterRef.current) newsletterRef.current.value = ""; 
    } catch (err) {
      console.error("FAILED...", err);
      alert("Subscription failed. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  const features = [
    {
      icon: "🤖",
      title: "Predictive Co-Pilot",
      description: "An AI-guided assistant that forecasts experimental outcomes and thermodynamic stability, reducing physical R&D iterations by up to 70%."
    },
    {
      icon: "🧬",
      title: "Materials Informatics",
      description: "Leveraging state-of-the-art generative models to navigate the combinatorial explosion of chemistry with high-fidelity accuracy."
    },
    {
      icon: "🌐",
      title: "Materials Knowledge Center",
      description: "A single source of truth for your R&D data, preserving institutional memory and eliminating fragmented research silos."
    },
    {
      icon: "📦",
      title: "High-Throughput Design",
      description: "Digitally screen and generate up to 100 computationally validated crystal structures per batch for rapid material discovery."
    },
    {
      icon: "📊",
      title: "Digital Twin Visualization",
      description: "Interactive 3D structural analysis for confirmed candidates, featuring full API connectivity to existing enterprise systems (LIMS/ELN)."
    },
    {
      icon: "⚡",
      title: "Digital R&D Foundation",
      description: "A cloud-native infrastructure built to modernize legacy discovery workflows through data standardization and automation."
    }
  ];

  const useCases = [
    {
      title: "Next-Gen Energy Storage",
      description: "Harnessing Materials Informatics to discover stable Li-O structures. Identifying novel electrolytes and catalysts to overcome cycle-life degradation in the post-Li-ion era."
    },
    {
      title: "Supply Chain Sovereignty",
      description: "Digitizing the search for earth-abundant alternatives. Designing Fe-N phases to eliminate Rare Earth (REE) dependence and reduce geopolitical risk."
    },
    {
      title: "Closed-Loop Validation",
      description: (
        <>
          Bridging AI and the laboratory. Filtering 10,000 candidates to the top 0.1% (E<sub>hull</sub> &lt; 0.02 eV/atom) to eliminate 99% of R&D 'sunk costs'.
        </>
      )
    }
  ];

  return (
    <div className="font-sans text-gray-900 dark:text-gray-100 transition-colors duration-300">
      {/* Section Navigation */}
      <Navbar theme={theme} onToggleTheme={toggleTheme} />

      {/* Scroll to Top Button */}
      <button 
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 p-4 rounded-full bg-brandTeal text-white shadow-2xl hover:bg-black dark:hover:bg-teal-700 transition-all duration-300 focus:outline-none ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12 pointer-events-none'}`}
        aria-label="Scroll to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>

      {/* 1. Hero Section */}
      <header id="top" className="relative min-h-screen flex items-center overflow-hidden pt-16 md:pt-20 border-b-[12px] border-gray-100 dark:border-black">
        <div className="absolute inset-0 z-0">
          <img src="/grid.jpg" alt="AI Data" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80 dark:bg-brandDark/90 backdrop-blur-[2px]"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-12 relative z-10 grid md:grid-cols-2 gap-12 items-center">
          <div className="text-center md:text-left">
            <Reveal delay={0.2}>
              <div className="mb-8 flex justify-center md:justify-start relative inline-flex">
                <img src={theme === 'dark' ? '/logo-artwork-hollow-dark.png' : '/logo-artwork-hollow-light.png'} alt="Aether logo" className="w-48 md:w-56 lg:w-64 h-auto drop-shadow-2xl" />
                <span className="absolute top-0 right-0 translate-x-[25%] -translate-y-[2%] text-gray-900 dark:text-white text-lg md:text-xl lg:text-2xl font-black leading-none z-10 select-none">™</span>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <h1 className="text-4xl md:text-6xl lg:text-8xl text-gray-900 dark:text-white font-black mb-4 uppercase tracking-tighter leading-none">
                Aether
              </h1>
            </Reveal>
            <Reveal delay={0.6}>
              <p className="mb-8 text-brandTeal text-lg md:text-2xl font-light uppercase tracking-[0.3em]">
                a product by MatterAlchemy<sup className="text-[0.5em] font-bold align-super leading-none">TM</sup>
              </p>
            </Reveal>
            <Reveal delay={0.8}>
              <p className="max-w-lg mb-10 text-gray-600 dark:text-gray-400 text-sm md:text-lg leading-relaxed font-light mx-auto md:mx-0">
                The predictive co-pilot for high-throughput material discovery. Standardizing the path from computational prediction to lab-ready validation.
              </p>
            </Reveal>
            <Reveal delay={1.0}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a href="#connect" className="bg-brandTeal text-white px-10 py-5 text-sm font-black tracking-[0.2em] hover:bg-black dark:hover:bg-teal-700 transition shadow-2xl uppercase">
                  REQUEST A DEMO
                </a>
                <a href="#pipeline" className="bg-transparent border-2 border-brandTeal text-brandTeal px-10 py-5 text-sm font-black tracking-[0.2em] hover:bg-brandTeal hover:text-white transition uppercase">
                  EXPLORE PIPELINE
                </a>
              </div>
            </Reveal>
            <Reveal delay={1.4}>
              <div className="mt-10 flex items-center gap-3 justify-center md:justify-start">
                <img src="/nvidia-inception-badge.svg" alt="NVIDIA Inception program member badge" className="h-10 md:h-12 w-auto" />
                <span className="text-xs font-bold tracking-wide text-gray-500 dark:text-gray-400">NVIDIA Inception program member</span>
              </div>
            </Reveal>
          </div>
          
          <Reveal delay={1.2} className="hidden md:block">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-brandTeal to-blue-600 rounded-3xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
              <div className="relative bg-white dark:bg-brandLight p-2 rounded-3xl shadow-2xl">
                <img src="/cif_viewer_clean.png" alt="Aether Material Designer - Final Structure" className="rounded-2xl shadow-inner" />
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      {/* 2. Problem: The R&D Bottleneck */}
      <section className="bg-white dark:bg-brandDark py-12 md:py-24 px-4 md:px-6 border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light mb-6 md:mb-8 text-gray-900 dark:text-white">The R&D Bottleneck</h2>
            <p className="text-base md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              Material discovery is trapped in manual, spreadsheet-driven cycles. 
              Traditional lab methods face a 99% failure rate, costing millions in institutional knowledge loss 
              and wasted lab iterations. In the race for electrification, trial-and-error is no longer a viable strategy.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 3. Solution: The Aether Solution */}
      <section className="bg-gray-50 dark:bg-brandDark py-12 md:py-24 px-4 md:px-6 border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-light mb-6 md:mb-8 text-gray-900 dark:text-white">The Aether Solution</h2>
            <p className="text-lg md:text-2xl text-brandTeal font-light leading-relaxed mb-6 md:mb-8">
              Digitizing R&D. From Computational Prediction to Autonomous Delivery.
            </p>
            <p className="text-sm md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              Aether is a Predictive Co-Pilot 
              that collapses decades of R&D into weeks. We provide structurally validated, lab-ready candidates 
              while preserving organizational memory for every discovery.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 4. Vision: The Digital Foundation */}
      <section className="grid md:grid-cols-2 items-center overflow-hidden border-b-8 border-gray-100 dark:border-black bg-white dark:bg-brandDark">
        <Reveal><img src="/cityLights.jpg" alt="Future City" className="w-full h-48 md:h-auto object-cover" /></Reveal>
        <div className="p-8 md:p-20">
          <Reveal delay={0.2}>
            <h2 className="text-2xl md:text-4xl mb-4 md:mb-6 text-gray-900 dark:text-white font-light">Our Vision</h2>
            <p className="text-brandTeal text-sm md:text-base leading-relaxed max-w-md mb-6">
              Aether is building the digital foundation for the next industrial revolution. We are transforming the physical world of materials into a scalable, generative digital data stream.
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-sm md:text-base leading-relaxed max-w-md">
              From autonomous discovery to enterprise-grade informatics, we're empowering the teams building the future of energy, mobility, and aerospace.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 5. Features: Materials Informatics Platform */}
      <section id="platform" className="py-12 md:py-24 px-4 md:px-6 bg-white dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-10 md:mb-16 text-gray-900 dark:text-white">Materials Informatics Platform</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <Reveal key={index} delay={index * 0.1}>
                <div className="bg-gray-50 dark:bg-brandLight p-6 md:p-8 rounded-lg hover:shadow-lg dark:hover:bg-gray-800 transition">
                  <div className="text-3xl md:text-4xl mb-3 md:mb-4">{feature.icon}</div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base leading-relaxed">{feature.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Use Cases: Innovation Verticals */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-gray-50 dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-10 md:mb-16 text-gray-900 dark:text-white">Innovation Verticals</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {useCases.map((useCase, index) => (
              <Reveal key={index} delay={index * 0.1}>
                <div className="bg-white dark:bg-brandLight p-6 md:p-8 rounded-lg hover:shadow-lg transition border border-gray-200 dark:border-gray-700 hover:border-brandTeal dark:hover:border-brandTeal">
                  <div className="text-3xl md:text-4xl mb-3 md:mb-4 text-brandTeal">{index + 1}</div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3">{useCase.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base leading-relaxed">{useCase.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Proof: AI-Guided Discovery Loop */}
      <section id="discovery" className="py-12 md:py-24 px-4 md:px-6 bg-white dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-12 md:mb-16 text-gray-900 dark:text-white uppercase tracking-[0.2em]">Discovery Showcase</h2>
          </Reveal>
          
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* Main Demo Focus */}
            <div className="lg:col-span-8 space-y-8">
              <Reveal delay={0.2}>
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-brandTeal to-blue-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
                  <div className="relative bg-white dark:bg-brandLight p-2 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800">
                    <video 
                      src="/AetherWalkthroughDemo.mp4" 
                      controls
                      className="w-full h-auto rounded-2xl shadow-inner"
                    >
                      Your browser does not support the video tag.
                    </video>
                    <div className="absolute top-6 right-6 bg-brandTeal text-white text-[10px] font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-xl">
                      Demo Sample
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* Gallery of Results */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { img: "/discovery_step1.png", label: "Parameter Setup" },
                  { img: "/discovery_step2.png", label: "AI Inference" },
                  { img: "/discovery_step3.png", label: "Stability Analysis" },
                  { img: "/discovery_step4.png", label: "Final Candidates" }
                ].map((item, i) => (
                  <Reveal key={i} delay={0.4 + (i * 0.1)}>
                    <div className="group cursor-pointer" onClick={() => setSelectedImage(item.img)}>
                      <div className="relative overflow-hidden rounded-xl border border-gray-100 dark:border-gray-800 shadow-lg">
                        <img src={item.img} alt={item.label} className="w-full h-24 md:h-32 object-cover group-hover:scale-110 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-brandTeal/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-[8px] font-black uppercase tracking-tighter">View Large</span>
                        </div>
                      </div>
                      <p className="mt-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">{item.label}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Workflow Detail */}
            <div className="lg:col-span-4 space-y-8">
              <Reveal delay={0.4}>
                <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">Closed-Loop Discovery</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm md:text-base font-light mt-4">
                  Aether standardizes R&D by collapsing the discovery timeline from years to weeks. Our platform ensures every candidate is thermodynamically validated before it ever reaches the lab.
                </p>
              </Reveal>

              <div className="space-y-4">
                {[
                  { step: "INGEST", desc: "Standardize legacy R&D and literature data." },
                  { step: "PREDICT", desc: "Model creates 10,000 tailored candidates." },
                  { step: "FILTER", desc: "Simulation eliminates 99.9% of non-viable candidates." },
                  { step: "DELIVER", desc: "Lab-ready structural twins with stability scoring." }
                ].map((item, i) => (
                  <Reveal key={i} delay={0.6 + (i * 0.1)}>
                    <div className="flex gap-4 items-center p-5 bg-gray-50 dark:bg-brandLight rounded-2xl border border-transparent hover:border-brandTeal/30 transition-all cursor-default group">
                      <div className="w-8 h-8 rounded-full bg-brandTeal/10 flex items-center justify-center text-brandTeal font-black text-xs group-hover:bg-brandTeal group-hover:text-white transition-colors">{i + 1}</div>
                      <div>
                        <div className="text-brandTeal font-black text-[10px] uppercase tracking-widest">{item.step}</div>
                        <p className="text-gray-600 dark:text-gray-400 text-xs font-light">{item.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              
              <Reveal delay={1.2}>
                <div className="p-6 bg-brandTeal/5 rounded-3xl border border-brandTeal/20">
                  <p className="text-brandTeal text-xs font-bold italic text-center">
                    "Aether collapses 5 years of traditional discovery into 5 weeks of validated digital inference."
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 8. ROI: The Aether Advantage */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-gray-50 dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-10 md:mb-16 text-gray-900 dark:text-white">The Aether Advantage</h2>
          </Reveal>
          <div className="overflow-x-auto bg-white dark:bg-brandLight rounded-2xl shadow-xl p-4 md:p-8">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-4 px-4 text-gray-500 dark:text-gray-400 font-bold text-xs md:text-sm uppercase tracking-widest">Target Metric</th>
                  <th className="py-4 px-4 text-gray-500 dark:text-gray-400 font-bold text-xs md:text-sm uppercase tracking-widest">Traditional Approach</th>
                  <th className="py-4 px-4 text-brandTeal font-bold text-xs md:text-sm uppercase tracking-widest">Aether MaaS</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-brandDark/50 transition-colors">
                  <td className="py-6 px-4 text-gray-900 dark:text-white font-medium text-sm md:text-base">Discovery Cycles</td>
                  <td className="py-6 px-4 text-gray-600 dark:text-gray-400 text-sm md:text-base">3–5 years per candidate</td>
                  <td className="py-6 px-4 text-brandTeal text-sm md:text-base font-bold italic">3–5 weeks to validation (5x Speed)</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-brandDark/50 transition-colors">
                  <td className="py-6 px-4 text-gray-900 dark:text-white font-medium text-sm md:text-base">Physical Iterations</td>
                  <td className="py-6 px-4 text-gray-600 dark:text-gray-400 text-sm md:text-base">High-cost trial-and-error</td>
                  <td className="py-6 px-4 text-brandTeal text-sm md:text-base font-bold italic">70% Fewer Lab Synthesis Cycles</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-brandDark/50 transition-colors">
                  <td className="py-6 px-4 text-gray-900 dark:text-white font-medium text-sm md:text-base">Data Integrity</td>
                  <td className="py-6 px-4 text-gray-600 dark:text-gray-400 text-sm md:text-base">Fragmented spreadsheets</td>
                  <td className="py-6 px-4 text-brandTeal text-sm md:text-base font-bold italic">Unified Organizational Memory</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-brandDark/50 transition-colors">
                  <td className="py-6 px-4 text-gray-900 dark:text-white font-medium text-sm md:text-base">Sourcing Risk</td>
                  <td className="py-6 px-4 text-gray-600 dark:text-gray-400 text-sm md:text-base">Supply chain REE vulnerability</td>
                  <td className="py-6 px-4 text-brandTeal text-sm md:text-base font-bold italic">AI-Guided Substitution (Cobalt-free)</td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-brandDark/50 transition-colors">
                  <td className="py-6 px-4 text-gray-900 dark:text-white font-medium text-sm md:text-base">Digital Foundry</td>
                  <td className="py-6 px-4 text-gray-600 dark:text-gray-400 text-sm md:text-base">Isolated R&D Silos</td>
                  <td className="py-6 px-4 text-brandTeal text-sm md:text-base font-bold italic">10,000+ Parallel Digital Simulations</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 9. Business Model: Material-as-a-Service */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-4 text-gray-900 dark:text-white">Material-as-a-Service</h2>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-10 md:mb-16 text-sm md:text-base">The Digital Foundation for Discovery to Delivery</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <Reveal delay={0.1}>
              <div className="bg-gray-50 dark:bg-brandLight p-6 md:p-8 rounded-lg border-2 border-brandTeal relative shadow-sm">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brandTeal text-white text-xs font-bold px-3 py-1 rounded-full">TIER 1</div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3">Predictive Co-Pilot</h3>
                <p className="text-brandTeal text-xs font-bold mb-4 uppercase tracking-wider">Materials Informatics SaaS</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">Self-serve access via multi-tenant subdomains. Ingest legacy data and run autonomous structural inferences on Azure ML.</p>
                <ul className="text-gray-600 dark:text-gray-400 text-sm space-y-2">
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Compute-based pricing</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Data standardization / Source of Truth</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> E<sub>hull</sub> stability filtering</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> API connectivity to LIMS/ELN</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="bg-white dark:bg-brandLight p-6 md:p-8 rounded-lg border border-gray-200 dark:border-gray-700 relative shadow-sm">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-bold px-3 py-1 rounded-full">TIER 2</div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3">Digital Foundry</h3>
                <p className="text-brandTeal text-xs font-bold mb-4 uppercase tracking-wider">Internal R&D / IP Licensing</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">We patent internal breakthrough discoveries (e.g. Li-O/Fe-N). Clients license pre-validated structural digital twins.</p>
                <ul className="text-gray-600 dark:text-gray-400 text-sm space-y-2">
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Upfront licensing + royalties</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Pre-validated structural candidates</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Institutional memory preservation</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Ready for physical lab testing</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="bg-white dark:bg-brandLight p-6 md:p-8 rounded-lg border border-gray-200 dark:border-gray-700 relative shadow-sm">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-bold px-3 py-1 rounded-full">TIER 3</div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-3">Enterprise JDAs</h3>
                <p className="text-brandTeal text-xs font-bold mb-4 uppercase tracking-wider">Joint Development Partner</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">Deep collaboration using custom adapters fine-tuned on proprietary client (often failed) experimental data.</p>
                <ul className="text-gray-600 dark:text-gray-400 text-sm space-y-2">
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Proprietary model fine-tuning</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Secure, isolated digital environment</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Milestone-based strategic payouts</li>
                  <li className="flex items-center gap-2"><span className="text-brandTeal">✓</span> Multi-year strategic roadmap</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 10. Technical Depth: Discovery Pipeline */}
      <section id="pipeline" className="py-12 md:py-24 px-4 md:px-6 bg-gray-50 dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-4 text-gray-900 dark:text-white uppercase tracking-[0.2em]">Discovery Pipeline</h2>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-10 md:mb-16 text-sm md:text-base italic">From constraint to validated candidate in 5 automated steps</p>
          </Reveal>
          <div className="relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-6 left-0 w-full h-0.5 bg-gray-200 dark:bg-gray-800 -z-0"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4 relative z-10">
              {[
                { step: "1", title: "Define Constraints", desc: <>Select elements, target properties, E<sub>hull</sub> threshold</> },
                { step: "2", title: "AI Generation", desc: "Diffusion model creates 100 novel crystal structures" },
                { step: "3", title: "Stability Filter", desc: <>Calculate E<sub>hull</sub> for each candidate</> },
                { step: "4", title: "Candidate Ranking", desc: "Rank by thermodynamic stability" },
                { step: "5", title: "Validation Ready", desc: "Export CIF files, lab-ready for synthesis" }
              ].map((item, index) => (
                <Reveal key={index} delay={index * 0.1}>
                  <div className="text-center group">
                    <div className="w-12 h-12 mx-auto mb-6 rounded-full bg-brandTeal text-white text-xl font-bold flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 ring-4 ring-white dark:ring-brandDark">
                      {item.step}
                    </div>
                    <h3 className="text-gray-900 dark:text-white font-black mb-3 text-xs md:text-sm uppercase tracking-wider">{item.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-[10px] md:text-xs leading-relaxed max-w-[150px] mx-auto">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 11. Strategic Value: Compliance & ESG Strategy */}
      <section id="esg" className="py-12 md:py-24 px-4 md:px-6 bg-white dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-4 text-gray-900 dark:text-white uppercase tracking-[0.2em]">Compliance & ESG Strategy</h2>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-10 md:mb-16 text-sm md:text-base italic">De-Risking the Supply Chain through Digital Innovation</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
            <Reveal>
              <div className="bg-gray-50 dark:bg-brandLight p-8 md:p-12 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800">
                <div className="text-5xl mb-6">🌍</div>
                <h3 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white mb-6 uppercase tracking-tighter">Strategic Sovereignty</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm md:text-lg leading-relaxed mb-6 font-light">
                  As global regulations tighten on rare earth mining and "conflict minerals," the ability to design alternatives digitally is a critical competitive advantage.
                </p>
                <p className="text-gray-600 dark:text-gray-400 text-sm md:text-lg leading-relaxed font-light">
                  Aether enables organizations to automate the search for earth-abundant alternatives, providing quantifiable ESG metrics and supply chain predictability from the earliest stages of R&D.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="space-y-8">
                {[
                  { title: "Eliminate Rare Earth Vulnerability", desc: "Discover Fe-N alternatives to high-HHI magnets" },
                  { title: "Geopolitical Risk Mitigation", desc: "Transition to domestic, earth-abundant sourcing" },
                  { title: "Sustainability Storytelling", desc: "Quantifiable ESG metrics for stakeholders" },
                  { title: "Cost Predictability", desc: "Stable pricing, no commodity volatility" }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 items-start group">
                    <div className="text-brandTeal text-3xl shrink-0 group-hover:scale-125 transition-transform duration-200">✓</div>
                    <div>
                      <h4 className="text-gray-900 dark:text-white font-black text-sm md:text-lg mb-2 uppercase tracking-tight">{item.title}</h4>
                      <p className="text-gray-500 dark:text-gray-400 text-xs md:text-base font-light">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 12. Proof: Recognitions & Updates */}
      <section id="recognitions" className="py-12 md:py-24 px-4 md:px-6 bg-white dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-4 text-gray-900 dark:text-white uppercase tracking-[0.2em]">
              Recognitions &amp; Updates
            </h2>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-10 md:mb-16 text-sm md:text-base italic">
              Presenting our work within the scientific and research community
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {updates.map((update, index) => (
              <Reveal key={update.id} delay={index * 0.1}>
                <article className="h-full flex flex-col bg-gray-50 dark:bg-brandLight rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-lg transition-shadow">
                  <button
                    type="button"
                    onClick={() => setSelectedImage(update.fullImage)}
                    className="group relative block w-full bg-white border-b border-gray-100 dark:border-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brandTeal"
                    aria-label={`View the ${update.title} certificate`}
                  >
                    <img
                      src={update.image}
                      alt={`${update.title} presented by ${update.presenter ?? 'MatterAlchemy'}`}
                      loading="lazy"
                      className="w-full aspect-[1.414/1] object-contain"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-brandTeal/20 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="bg-white/95 dark:bg-brandDark/95 text-gray-900 dark:text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full shadow">
                        View Certificate
                      </span>
                    </span>
                  </button>

                  <div className="flex flex-col flex-grow p-6 md:p-8">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <span className="text-[10px] font-black uppercase tracking-widest text-brandTeal bg-brandTeal/10 px-3 py-1 rounded-full">
                        {update.category}
                      </span>
                      <time dateTime={update.date} className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        {update.displayDate}
                      </time>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2">{update.title}</h3>
                    <p className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
                      {update.issuer}
                    </p>
                    {update.presenter && (
                      <p className="text-sm font-bold text-brandTeal mb-3">{update.presenter}</p>
                    )}
                    {update.description && (
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">
                        {update.description}
                      </p>
                    )}
                    <div className="mt-auto flex items-center gap-6 text-sm">
                      <button
                        type="button"
                        onClick={() => setSelectedImage(update.fullImage)}
                        className="text-brandTeal font-bold hover:text-black dark:hover:text-white transition-colors"
                      >
                        View certificate
                      </button>
                      {update.pdf && (
                        <a
                          href={update.pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-gray-500 dark:text-gray-400 font-bold hover:text-brandTeal transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          PDF
                        </a>
                      )}
                      {update.href && (
                        <a
                          href={update.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 dark:text-gray-400 font-bold hover:text-brandTeal transition-colors"
                        >
                          Verify
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 13. About: Meet the Founders */}
      <section id="founders" className="py-12 md:py-24 px-4 md:px-6 bg-gray-50 dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-4xl font-light text-center mb-4 text-gray-900 dark:text-white">
              Meet the Founders
            </h2>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-10 md:mb-16 text-sm md:text-base">
              Built by domain experts with deep roots in AI infrastructure and enterprise software
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Hemanth Shetty */}
            <Reveal delay={0.1}>
              <div className="bg-white dark:bg-brandLight p-8 md:p-10 rounded-2xl border border-gray-100 dark:border-gray-800 hover:shadow-lg transition-shadow text-center">
                <img
                  src="/Hemanth-Photo.png"
                  alt="Hemanth Shetty"
                  className="w-28 h-28 mx-auto mb-6 rounded-full object-cover border-2 border-gray-100 dark:border-gray-700"
                />
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1">Hemanth Shetty</h3>
                <p className="text-brandTeal text-sm font-bold uppercase tracking-widest mb-4">CEO & Co-founder</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">
                  Brings deep expertise in building scalable AI/ML platforms, previously at Microsoft driving enterprise-grade software solutions. Holds an MS in Computer Science from Northeastern University with prior experience at VMware, New York Life Insurance Labs, and Tesco. Leads product vision, fundraising, and go-to-market strategy.
                </p>
                <a
                  href="https://www.linkedin.com/in/hemanth-shetty-neu/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-brandTeal hover:text-black dark:hover:text-white transition-colors text-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
              </div>
            </Reveal>

            {/* Girish Modiletappa */}
            <Reveal delay={0.2}>
              <div className="bg-white dark:bg-brandLight p-8 md:p-10 rounded-2xl border border-gray-100 dark:border-gray-800 hover:shadow-lg transition-shadow text-center">
                <img
                  src="/girish-profile.jpg"
                  alt="Girish Modiletappa"
                  className="w-28 h-28 mx-auto mb-6 rounded-full object-cover border-2 border-gray-100 dark:border-gray-700"
                />
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1">Girish Modiletappa</h3>
                <p className="text-brandTeal text-sm font-bold uppercase tracking-widest mb-4">CTO & Co-founder</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">
                  Over a decade of software engineering experience across enterprise, government, and open-source ecosystems. Built OpenProject integrations, developed microservices at Cisco (WebEx Teams), contributed to the TMT observatory project at IIA, and TA at IISc. Architects the Aether platform and leads engineering delivery.
                </p>
                <a
                  href="https://www.linkedin.com/in/girish-m-a1b09b333/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-brandTeal hover:text-black dark:hover:text-white transition-colors text-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 14. Connect: Connect with Us */}
      <section id="connect" className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 items-center bg-white dark:bg-brandDark border-b-8 border-gray-100 dark:border-black">
        <Reveal>
          <div className="space-y-8">
            <h3 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">Connect with Us</h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm md:text-lg font-light leading-relaxed">
              Ready to modernize your R&D discovery loop? Reach out to our team for a custom demonstration tailored to your material targets.
            </p>
            <div className="space-y-6 pt-4">
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=Manyata+Redwood+9th+Floor+Manyata+Tech+Park+Bangalore" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-brandTeal/10 flex items-center justify-center text-brandTeal hover:bg-brandTeal hover:text-white transition-all shadow-sm"
                  title="Open in Google Maps"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </a>
                <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Manyata Redwood, 9th Floor, Block D3, Manyata Tech Park, Thanisandra, Bangalore – 560 045</span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="bg-gray-50 dark:bg-brandLight p-8 md:p-12 rounded-[2.5rem] shadow-2xl border border-gray-100 dark:border-gray-800">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-1">
                <label htmlFor="from_name" className="text-[10px] font-black text-brandTeal uppercase tracking-widest ml-1">Full Name</label>
                <input id="from_name" {...register("from_name")} placeholder="Your Name" aria-invalid={!!errors.from_name} aria-describedby={errors.from_name ? "from_name-error" : undefined} className="w-full bg-white dark:bg-brandDark rounded-2xl px-6 py-4 outline-none focus:ring-2 focus:ring-brandTeal/50 text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 transition-all border border-gray-100 dark:border-gray-800" />
                {errors.from_name && (
                  <p id="from_name-error" role="alert" className="text-xs font-semibold text-red-500 dark:text-red-400 ml-1">{errors.from_name.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <label htmlFor="reply_to" className="text-[10px] font-black text-brandTeal uppercase tracking-widest ml-1">Email Address</label>
                <input id="reply_to" {...register("reply_to")} placeholder="your@email.com" aria-invalid={!!errors.reply_to} aria-describedby={errors.reply_to ? "reply_to-error" : undefined} className="w-full bg-white dark:bg-brandDark rounded-2xl px-6 py-4 outline-none focus:ring-2 focus:ring-brandTeal/50 text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 transition-all border border-gray-100 dark:border-gray-800" />
                {errors.reply_to && (
                  <p id="reply_to-error" role="alert" className="text-xs font-semibold text-red-500 dark:text-red-400 ml-1">{errors.reply_to.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <label htmlFor="message" className="text-[10px] font-black text-brandTeal uppercase tracking-widest ml-1">Message</label>
                <textarea id="message" {...register("message")} placeholder="Tell us about your material targets..." aria-invalid={!!errors.message} aria-describedby={errors.message ? "message-error" : undefined} rows={4} className="w-full bg-white dark:bg-brandDark rounded-2xl px-6 py-4 outline-none focus:ring-2 focus:ring-brandTeal/50 text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 transition-all border border-gray-100 dark:border-gray-800 resize-none" />
                {errors.message && (
                  <p id="message-error" role="alert" className="text-xs font-semibold text-red-500 dark:text-red-400 ml-1">{errors.message.message}</p>
                )}
              </div>
              <button disabled={isSubmitting} className="w-full bg-brandTeal text-white py-5 rounded-2xl font-black tracking-[0.2em] uppercase hover:bg-black dark:hover:bg-teal-700 transition shadow-xl active:scale-[0.98] disabled:opacity-50">
                {isSubmitting ? "Sending..." : "Send Request"}
              </button>
            </form>
          </div>
        </Reveal>
      </section>

      {/* 15. Footer */}
      <footer className="bg-gray-50 dark:bg-brandDark text-gray-900 dark:text-white">
        <div className="py-24 md:py-32 bg-white/40 dark:bg-black/20 px-4 md:px-6 relative overflow-hidden text-center">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brandTeal to-transparent"></div>
            
            <Reveal>
              <h2 className="text-3xl md:text-5xl font-black mb-6 uppercase tracking-tighter">Join the Future of Material Science</h2>
              <p className="text-brandTeal text-lg md:text-xl font-light italic mb-12">Aether by MatterAlchemy<sup className="text-[0.45em] font-bold">TM</sup></p>
              
              <form onSubmit={onJoinNow} className="max-w-2xl mx-auto flex flex-col md:flex-row gap-4 p-2 bg-white dark:bg-brandLight rounded-[2rem] shadow-2xl border border-gray-100 dark:border-gray-800">
                <input ref={newsletterRef} type="email" required placeholder="Enter your email" className="flex-grow bg-transparent px-8 py-4 text-black dark:text-white outline-none" />
                <button type="submit" disabled={isSubscribing} className="bg-brandTeal px-10 py-4 rounded-3xl font-black tracking-widest uppercase text-white hover:bg-black dark:hover:bg-teal-700 transition disabled:opacity-50">
                  {isSubscribing ? "Joining..." : "Early Access"}
                </button>
              </form>
            </Reveal>
        </div>
        <div className="py-12 px-4 md:px-12 border-t border-gray-100 dark:border-gray-800 flex flex-col items-center gap-6 text-[10px] tracking-[0.2em] uppercase">
            <div className="flex flex-col items-center gap-2">
              <img src="/nvidia-inception-badge.svg" alt="NVIDIA Inception program member badge" className="h-14 md:h-16 w-auto" />
              <p className="opacity-60 normal-case tracking-normal">MatterAlchemy LLP is a member of the NVIDIA Inception program.</p>
            </div>
            <p className="opacity-60 normal-case tracking-normal">© 2026 MatterAlchemy LLP. All rights reserved. MatterAlchemy™ and the MatterAlchemy logo are trademarks of MatterAlchemy LLP.</p>
            <p className="normal-case tracking-normal max-w-3xl text-center leading-relaxed opacity-60">© 2025 NVIDIA, the NVIDIA logo, and NVIDIA Inception are trademarks and/or registered trademarks of NVIDIA Corporation in the U.S. and other countries.</p>
            <p className="normal-case tracking-normal max-w-3xl text-center leading-relaxed opacity-60">MatterAlchemy LLP is recognized as a startup by DPIIT, Ministry of Commerce & Industry, Government of India (DIPP284131).</p>
            <div className="flex gap-8 opacity-60">
              <button onClick={() => setShowTerms(true)} className="hover:text-brandTeal transition cursor-pointer">Terms</button>
              <button onClick={() => setShowPrivacy(true)} className="hover:text-brandTeal transition cursor-pointer">Privacy</button>
            </div>
        </div>
      </footer>

      {/* Modals Container */}
      <div className="relative z-[100]">
        {/* Lightbox Modal */}
        {(() => {
          const AP = AnimatePresence as any;
          return (
            <AP>
              {selectedImage && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setSelectedImage(null)}
                  className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-12 cursor-zoom-out"
                >
                  <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="relative max-w-7xl w-full h-full flex items-center justify-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img 
                      src={selectedImage} 
                      alt="Large view" 
                      className="max-w-full max-h-full object-contain rounded-xl shadow-2xl border border-white/10" 
                    />
                    <button 
                      onClick={() => setSelectedImage(null)}
                      className="absolute top-0 right-0 m-4 text-white/50 hover:text-white transition-colors"
                    >
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AP>
          );
        })()}

        {/* Terms Modal */}
        {(() => {
          const AP = AnimatePresence as any;
          return (
            <AP>
              {showTerms && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowTerms(false)}
                  className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
                >
                  <motion.div 
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 50, opacity: 0 }}
                    className="bg-white dark:bg-brandLight max-w-2xl w-full max-h-[80vh] overflow-y-auto rounded-3xl p-8 md:p-12 shadow-2xl relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button onClick={() => setShowTerms(false)} className="absolute top-6 right-6 text-gray-400 hover:text-brandTeal transition-colors">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <h2 className="text-2xl font-black uppercase tracking-tighter mb-6 text-gray-900 dark:text-white">Terms of Service</h2>
                    <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      <p className="font-bold text-gray-900 dark:text-white">Last Updated: March 30, 2026</p>
                      <p>Welcome to Aether, a platform by MatterAlchemy. By accessing our services, you agree to comply with and be bound by the following terms and conditions.</p>
                      <h3 className="font-bold text-gray-900 dark:text-white uppercase tracking-widest text-xs mt-6">1. Intellectual Property</h3>
                      <p>All computational models, generated structural data, and platform interfaces are the exclusive property of MatterAlchemy or its licensors. Unauthorized reproduction or reverse engineering is strictly prohibited.</p>
                      <h3 className="font-bold text-gray-900 dark:text-white uppercase tracking-widest text-xs mt-6">2. Service Usage</h3>
                      <p>Aether provides materials informatics predictions. While we utilize state-of-the-art, computational predictions should be validated in a physical laboratory environment before industrial application.</p>
                      <h3 className="font-bold text-gray-900 dark:text-white uppercase tracking-widest text-xs mt-6">3. Data Security</h3>
                      <p>We maintain enterprise-grade security for all client data. Users are responsible for maintaining the confidentiality of their account credentials.</p>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AP>
          );
        })()}

        {/* Privacy Modal */}
        {(() => {
          const AP = AnimatePresence as any;
          return (
            <AP>
              {showPrivacy && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowPrivacy(false)}
                  className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
                >
                  <motion.div 
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 50, opacity: 0 }}
                    className="bg-white dark:bg-brandLight max-w-2xl w-full max-h-[80vh] overflow-y-auto rounded-3xl p-8 md:p-12 shadow-2xl relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button onClick={() => setShowPrivacy(false)} className="absolute top-6 right-6 text-gray-400 hover:text-brandTeal transition-colors">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <h2 className="text-2xl font-black uppercase tracking-tighter mb-6 text-gray-900 dark:text-white">Privacy Policy</h2>
                    <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      <p className="font-bold text-gray-900 dark:text-white">Last Updated: March 30, 2026</p>
                      <p>At MatterAlchemy, we prioritize the privacy and security of your R&D data. This policy outlines how we handle information within the Aether platform.</p>
                      <h3 className="font-bold text-gray-900 dark:text-white uppercase tracking-widest text-xs mt-6">1. Data Collection</h3>
                      <p>We collect information necessary to provide materials informatics services, including user contact details and R&D parameters provided for computational inference.</p>
                      <h3 className="font-bold text-gray-900 dark:text-white uppercase tracking-widest text-xs mt-6">2. Information Usage</h3>
                      <p>Data is used exclusively to refine model accuracy for your specific R&D targets and to provide secure access to the Aether platform. We do not sell or share individual structural discovery data with third parties.</p>
                      <h3 className="font-bold text-gray-900 dark:text-white uppercase tracking-widest text-xs mt-6">3. Multi-Tenant Isolation</h3>
                      <p>The Aether architecture ensures strict data isolation between tenants. Your proprietary R&D data remains siloed within your dedicated environment.</p>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AP>
          );
        })()}
      </div>
    </div>
  );
}

export default App;
